import React, { useState, useEffect } from 'react';
import Loader from './Loader';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

function ClientOffer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [offerFirestoreId, setOfferFirestoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroImage, setHeroImage] = useState('/img/photos/default.jpg');
  
  const currentLang = i18n.language || 'bs';
  const [isPrivacyEnabled, setIsPrivacyEnabled] = useState(false);
  const [selectedExtraOffers, setSelectedExtraOffers] = useState([]);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [clientNotes, setClientNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [settings, setSettings] = useState(null);

  const formatDatum = (datum) => {
    if (!datum) return '';
    const date = new Date(datum);
    if (isNaN(date.getTime())) {
      return datum; 
    }
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        // Fetch Settings and Offer independently
        const [settingsResult, offerResult] = await Promise.allSettled([
          getDocs(collection(db, "settings")),
          getDocs(query(collection(db, "offers"), where("id", "==", id)))
        ]);

        if (settingsResult.status === 'fulfilled' && !settingsResult.value.empty) {
          setSettings(settingsResult.value.docs[0].data());
        }

        if (offerResult.status === 'fulfilled' && !offerResult.value.empty) {
          const docSnap = offerResult.value.docs[0];
          const offerData = docSnap.data();
          setOffer(offerData);
          setOfferFirestoreId(docSnap.id);
          setClientNotes(offerData.clientNotes || '');
          
          // Force language if specified by admin
          if (offerData.displayLanguage && i18n.language !== offerData.displayLanguage) {
            i18n.changeLanguage(offerData.displayLanguage);
          }
          
          setSelectedExtraOffers([]);
          
          const isMobile = window.innerWidth < 768;
          let imageName = isMobile
            ? (offerData.slikaMobile || offerData.slikaDesktop || offerData.slika || 'default.jpg')
            : (offerData.slikaDesktop || offerData.slikaMobile || offerData.slika || 'default.jpg');
          
          // Proširena lista defaultnih slika koje želimo zaobići ako postoji custom desktop slika
          const defaultImages = [
            'hero1d.jpg', 'hero2d.jpg', 'hero3d.jpg', 
            'hero1m.jpg', 'hero2m.jpg', 
            'default.jpg',
            '/img/photos/hero1d.jpg', '/img/photos/hero2d.jpg', '/img/photos/hero3d.jpg',
            '/img/photos/hero1m.jpg', '/img/photos/hero2m.jpg',
            '/img/photos/default.jpg'
          ];
          
          // Logika za fallback: ako je na mobilnom uređaju učitana neka od defaultnih slika, 
          // a na desktopu je odabrana custom slika (koja nije u listi defaultnih), koristi desktop sliku.
          if (isMobile) {
            const currentIsDefault = defaultImages.some(def => imageName === def);
            const desktopIsCustom = offerData.slikaDesktop && !defaultImages.some(def => offerData.slikaDesktop === def);
            
            if (currentIsDefault && desktopIsCustom) {
              imageName = offerData.slikaDesktop;
            }
          }
          
          if (imageName.includes('/') || imageName.startsWith('http')) {
               setHeroImage(imageName);
          } else {
               setHeroImage(`/img/photos/${imageName}`);
          }
        } else if (offerResult.status === 'fulfilled' && offerResult.value.empty) {
          setError(t('clientOffer.notFound'));
        } else {
          // If offerResult failed
          console.error("Error fetching offer:", offerResult.reason);
          setError(t('clientOffer.loadError'));
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Unexpected error in fetchOffer:", err);
        setError(t('clientOffer.loadError'));
        setLoading(false);
      }
    };

    fetchOffer();
  }, [id, i18n, t]);

  useEffect(() => {
    if (offer && offer.name) {
      const fullTitle = `${offer.name} - Nejira Zukić Weddings`;
      document.title = fullTitle;

      // Dinamički SEO i OpenGraph tagovi
      const updateMetaTag = (property, content, isName = false) => {
        const attribute = isName ? 'name' : 'property';
        let element = document.querySelector(`meta[${attribute}="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute(attribute, property);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      const description = `Vaša personalizovana ponuda za vjenčanje - Nejira Zukić Weddings. Paket: ${offer.nazivPaketa}.`;
      const logoUrl = "https://nejirazukic-weddings.web.app/img/NZ%20logo%20crni.png";

      updateMetaTag('og:title', fullTitle);
      updateMetaTag('og:description', description);
      updateMetaTag('og:image', logoUrl);
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('og:type', 'website');
      updateMetaTag('twitter:card', 'summary_large_image', true);
      updateMetaTag('twitter:site', '@nejirazukic', true); 
      updateMetaTag('twitter:title', fullTitle);
      updateMetaTag('twitter:description', description);
      updateMetaTag('twitter:image', logoUrl);
      updateMetaTag('description', description, true);
    } else {
      document.title = "Nejira Zukić Weddings";
    }
  }, [offer, heroImage]);

  const calculateTotal = () => {
    if (!offer) return 0;
    let total = parseFloat(offer.cijena) || 0;
    
    // If already accepted, use the extras saved in the offer
    const extrasToUses = (offer.status === 'prihvaćeno' && offer.clientSelectedExtras) 
      ? offer.clientSelectedExtras 
      : selectedExtraOffers;

    extrasToUses.forEach(extra => {
      total += parseFloat(extra.cijena) || 0;
    });

    const privacyWasRequested = offer.status === 'prihvaćeno' ? offer.privacyRequested : isPrivacyEnabled;

    if (privacyWasRequested && offer.privacyOption?.enabled) {
      total += parseFloat(offer.privacyOption.price) || 0;
    }
    
    return total;
  };

  const toggleExtraOffer = (extra) => {
    const exists = selectedExtraOffers.some(e => e.firestoreId === extra.firestoreId);
    if (exists) {
      setSelectedExtraOffers(selectedExtraOffers.filter(e => e.firestoreId !== extra.firestoreId));
    } else {
      setSelectedExtraOffers([...selectedExtraOffers, extra]);
    }
  };

  const handleDecision = async (status) => {
    if (!offerFirestoreId) return;
    setSubmitting(true);
    try {
      const finalReason = rejectReason === 'Drugo' ? otherReason : rejectReason;
      await updateDoc(doc(db, "offers", offerFirestoreId), {
        status: status,
        rejectionReason: status === 'odbijeno' ? finalReason : null,
        privacyRequested: isPrivacyEnabled,
        clientSelectedExtras: selectedExtraOffers,
        decisionDate: new Date()
      });

      // Update local state to reflect the change immediately
      setOffer(prev => ({
        ...prev,
        status: status,
        rejectionReason: status === 'odbijeno' ? finalReason : null,
        privacyRequested: isPrivacyEnabled,
        clientSelectedExtras: selectedExtraOffers
      }));

      // Send Telegram notification if configured
      if (settings?.telegramBotToken && settings?.telegramChatId) {
        try {
          const isAccepted = status === 'prihvaćeno';
          const icon = isAccepted ? '✅' : '❌';
          const total = calculateTotal();
          
          let telegramText = `
${icon} *Ponuda je ${status.toUpperCase()}*

👤 *Klijent:* ${offer.name}
📅 *Datum vjenčanja:* ${formatDatum(offer.datum)}
📦 *Glavni paket:* ${offer.nazivPaketa}

💰 *Ukupan iznos:* ${total} ${offer.valuta}
          `.trim();

          if (!isAccepted && finalReason) {
            telegramText += `\n💬 *Razlog odbijanja:* ${finalReason}`;
          }

          if (isAccepted) {
            if (selectedExtraOffers.length > 0) {
              telegramText += `\n➕ *Dodatne usluge:* ${selectedExtraOffers.map(e => typeof e.naziv === 'object' ? (e.naziv[currentLang] || e.naziv.bs) : e.naziv).join(', ')}`;
            }
            if (isPrivacyEnabled) {
              telegramText += `\n🔒 *Privatnost:* Zatražena (+${offer.privacyOption?.price} ${offer.privacyOption?.valuta})`;
            }
          }

          await fetch(`https://api.telegram.org/bot${settings.telegramBotToken}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chat_id: settings.telegramChatId,
              text: telegramText,
              parse_mode: 'Markdown'
            })
          });
        } catch (teleErr) {
          console.error("Telegram error:", teleErr);
        }
      }

      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      alert("Greška pri slanju.");
      setSubmitting(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!offerFirestoreId) return;
    setSavingNotes(true);
    try {
      await updateDoc(doc(db, "offers", offerFirestoreId), {
        clientNotes: clientNotes
      });

      // Send Telegram notification if configured
      if (settings?.telegramBotToken && settings?.telegramChatId && clientNotes) {
        try {
          const telegramText = `
📝 *Nove napomene od klijenta*

👤 *Klijent:* ${offer.name}
📦 *Paket:* ${offer.nazivPaketa}

💬 *Napomene:*
${clientNotes}
          `.trim();

          await fetch(`https://api.telegram.org/bot${settings.telegramBotToken}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chat_id: settings.telegramChatId,
              text: telegramText,
              parse_mode: 'Markdown'
            })
          });
        } catch (teleErr) {
          console.error("Telegram error:", teleErr);
        }
      }

      alert(t('clientOffer.notesSaved') || "Napomene su sačuvane!");
      setSavingNotes(false);
    } catch (err) {
      console.error(err);
      alert("Greška pri čuvanju napomena.");
      setSavingNotes(false);
    }
  };

  if (loading) return <Loader label={t('offers.loading')} />;
  if (error) return (
    <div className="h-screen flex items-center justify-center bg-[#f7f4f1] px-6 text-center text-sans">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2 font-serif">Ups!</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="border border-gray-900 text-gray-900 px-8 py-3 rounded-full font-medium tracking-[0.2em] text-[10px] hover:bg-gray-900 hover:text-white transition-all"
        >
          Pokušaj ponovo
        </button>
      </div>
    </div>
  );

  if (submitted || (offer && (offer.status === 'prihvaćeno' || offer.status === 'odbijeno'))) {
    const isAccepted = offer?.status === 'prihvaćeno';
    const isRejected = offer?.status === 'odbijeno';
    
    if (isRejected) {
      return (
        <div className="min-h-screen bg-[#fcfaf8] px-4 py-8 md:py-20 flex flex-col items-center justify-center">
          <div className="w-full max-w-lg bg-white p-8 md:p-12 rounded-[2.5rem] shadow-2xl border border-gray-100 text-center">
             <div className="text-4xl mb-6">🤍</div>
             
             <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 font-serif uppercase tracking-tight">
               {t('clientOffer.rejectThankYou')}
             </h2>
             
             <p className="text-gray-500 text-sm md:text-base mb-10 leading-relaxed font-medium">
               {t('clientOffer.rejectSuccessDesc')}
             </p>

             <button 
               onClick={() => navigate('/')} 
               className="inline-block px-10 py-4 bg-black text-white rounded-full text-[10px] font-black uppercase tracking-[0.2em] hover:bg-gray-800 transition-all shadow-xl shadow-black/10"
             >
               {t('clientOffer.backHome')}
             </button>
          </div>
        </div>
      );
    }

    if (isAccepted) {
      return (
        <div className="min-h-screen bg-[#fcfaf8] px-4 py-8 md:py-20 flex flex-col items-center">
          <div className="w-full max-w-2xl bg-white p-6 md:p-12 rounded-3xl shadow-2xl border-2 border-gray-900 relative">
             <div className="text-4xl mb-4 text-center">✨</div>
             
             <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-2 font-serif text-center uppercase tracking-tight">
               {t('clientOffer.thankYou')}
             </h2>
             
             <p className="text-gray-900 text-sm md:text-base mb-8 text-center px-4 font-bold opacity-70">
               {t('clientOffer.successDesc')}
             </p>

             <div className="space-y-6">
                <div className="flex justify-between items-end border-b-2 border-gray-900 pb-4 text-sans">
                   <div className="flex-1 min-w-0 mr-2">
                      <p className="text-[10px] font-black text-gray-400 tracking-widest mb-1 uppercase">Klijent</p>
                      <p className="text-2xl font-black text-gray-900 truncate font-serif">{offer?.name}</p>
                   </div>
                   <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-[10px] font-black tracking-widest mb-2 whitespace-nowrap uppercase shadow-lg">Prihvaćeno</span>
                </div>

                <div className="space-y-3">
                   <div className="flex justify-between items-start text-sm gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-900 font-black block break-words text-lg">{offer?.nazivPaketa}</span>
                        {(offer?.trajanje || offer?.trajanjePaketa) && (
                          <span className="text-[10px] font-black text-gray-500 tracking-wider uppercase border border-gray-200 px-2 py-0.5 rounded-md inline-block mt-1">
                            {typeof offer.trajanje === 'object' ? (offer.trajanje[currentLang] || offer.trajanje.bs) : (offer.trajanje || offer.trajanjePaketa)}
                          </span>
                        )}
                        {offer?.opis && (
                          <p className="text-[11px] text-gray-400 italic mt-2 leading-relaxed font-medium">
                            {typeof offer.opis === 'object' ? (offer.opis[currentLang] || offer.opis.bs) : offer.opis}
                          </p>
                        )}
                      </div>
                      <span className="font-black text-gray-900 text-xl whitespace-nowrap">{offer?.cijena} {offer?.valuta}</span>
                   </div>
                   
                   {(offer?.clientSelectedExtras || selectedExtraOffers).map((extra, idx) => (
                      <div key={idx} className="flex justify-between text-sm gap-3 pt-2 border-t border-gray-100">
                         <span className="text-gray-600 font-bold flex-1 min-w-0 break-words">+ {typeof extra.naziv === 'object' ? (extra.naziv[currentLang] || extra.naziv.bs) : extra.naziv}</span>
                         <span className="font-black text-gray-900 whitespace-nowrap">+{extra.cijena} {extra.valuta}</span>
                      </div>
                   ))}

                   {(offer?.privacyRequested || isPrivacyEnabled) && (
                      <div className="flex justify-between text-sm font-black text-emerald-700 pt-2 border-t border-gray-100">
                         <span>🔒 {t('clientOffer.privacyTitle')}</span>
                         <span>+{offer?.privacyOption?.price} {offer?.privacyOption?.valuta}</span>
                      </div>
                   )}

                   <div className="pt-5 border-t-2 border-gray-900 flex justify-between items-center">
                      <span className="text-lg font-black text-gray-900 uppercase tracking-tight">{t('clientOffer.totalInvestment')}</span>
                      <span className="text-4xl font-black text-gray-900 font-serif leading-none">{calculateTotal()} {offer?.valuta}</span>
                   </div>
                </div>

                <div className="bg-gray-100 p-6 rounded-2xl border-2 border-gray-200">
                   <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      SLJEDEĆI KORACI
                   </h4>
                   <ul className="space-y-3 text-[13px] text-gray-900 leading-snug font-bold">
                      <li className="flex gap-3 items-start">
                         <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0"></div>
                         <span>Rezervacija termina potvrđena. Kontaktirat ćemo vas putem E-maila.</span>
                      </li>
                      <li className="flex gap-3 items-start">
                         <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0"></div>
                         <span>Uputstva za tim su zabilježena. Radujemo se vjenčanju!</span>
                      </li>
                   </ul>
                </div>

                <div className="border-2 border-gray-900 rounded-2xl p-6 text-sans shadow-lg">
                   <label className="block text-[10px] font-black text-gray-900 tracking-widest mb-3 uppercase">
                      Dodatne napomene za fotografa
                   </label>
                   <textarea
                     value={clientNotes}
                     onChange={(e) => setClientNotes(e.target.value)}
                     placeholder="Obavezno nas fotkati sa nenom..."
                     className="w-full min-h-[100px] p-4 text-sm text-gray-900 bg-gray-50 border border-transparent focus:bg-white focus:border-gray-900 transition-all resize-none font-bold"
                   />
                   <button 
                     onClick={handleSaveNotes}
                     disabled={savingNotes}
                     className="mt-3 w-full bg-gray-900 text-white text-[11px] font-black tracking-[0.2em] py-4 rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 uppercase shadow-md"
                   >
                      {savingNotes ? 'Sačuvavanje...' : 'Sačuvaj izmjene'}
                   </button>
                </div>
             </div>

             <div className="mt-10 text-center">
               <button onClick={() => navigate('/')} className="text-[10px] text-gray-400 font-black hover:text-black tracking-[0.3em] transition-colors uppercase border-b border-transparent hover:border-black pb-1">
                 {t('clientOffer.backHome')}
               </button>
             </div>
          </div>
        </div>
      );
    }
  }

  if (!offer) return <p>{t('clientOffer.notFound')}</p>;

  const rejectReasons = [
    "Cijena je iznad našeg budžeta",
    "Termin je već popunjen kod drugog pružaoca usluga",
    "Pronašli smo ponudu koja nam više odgovara",
    "Odlučili smo se za drugačiji stil fotografisanja/videa",
    "Još uvijek nismo sigurni u datum vjenčanja",
    "Drugo"
  ];

  return (
    <section className="w-full relative">
      <div className="w-full h-[60vh] md:h-[70vh] relative flex items-center justify-center overflow-hidden p-0 m-0 text-sm md:text-base bg-black">
        <img
          src={heroImage}
          alt={`Nejira Zukić - Personalizovana ponuda za vjenčanje ${offer?.name} - Profesionalna fotografija i videografija`}
          className="select-none animate-ken-burns opacity-60"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '100vw',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            transform: 'translate(-50%, -50%)',
            minWidth: '100%',
            minHeight: '100%',
            zIndex: 0
          }}
          draggable="false"
        />
        <img src="/img/NZ logo crni.png" alt="Logo" className="absolute top-10 left-1/2 transform -translate-x-1/2 w-32 md:w-36 z-20 opacity-95 invert brightness-0" />
        <div className="absolute bottom-0 left-0 w-full h-1/2 z-10 pointer-events-none" style={{background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)'}} />
        <div
          className="absolute bottom-0 left-0 w-full z-20 flex flex-col items-center justify-end px-6 pb-10"
        >
          <span className="text-white/70 text-[10px] tracking-[0.4em] font-bold mb-2 opacity-0 animate-fade-in-up">{t('clientOffer.proposalFor')}</span>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 tracking-tight text-white text-center leading-none opacity-0 animate-fade-in-up"
            style={{ fontFamily: "'Playfair Display', serif", animationDelay: '0.2s', animationFillMode: 'forwards' }}
          >
            {offer.name}
          </h1>
          <div className="w-12 h-[1px] bg-white/30 mb-4 opacity-0 animate-fade-in" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}></div>
          <p
            className="text-base sm:text-lg md:text-xl font-medium text-white/80 text-center opacity-0 animate-fade-in-up"
            style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.05em', animationDelay: '0.5s', animationFillMode: 'forwards' }}
          >
            {formatDatum(offer.datum)}
          </p>
        </div>
      </div>

      <div className="w-full bg-[#fcfaf8] py-8 md:py-12 px-4 sm:px-6 md:px-10 lg:px-20 font-sans">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#ede3da] p-6 sm:p-10 md:p-12 relative overflow-hidden shadow-sm">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#fcfaf8] rounded-bl-full border-l border-b border-[#ede3da] opacity-50"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-gray-800">
              <div className="space-y-4">
                <h3 className="text-[10px] tracking-[0.3em] font-black text-gray-400 uppercase">{t('clientOffer.offeredPackage')}</h3>
                <p className="text-3xl font-bold font-serif leading-tight tracking-tight text-gray-900">
                  {offer.nazivPaketa}
                </p>
                {(offer.trajanje || offer.trajanjePaketa) && (
                   <span className="inline-block px-3 py-1 border-2 border-gray-900 rounded-full text-[10px] font-black text-gray-900 tracking-widest leading-none mt-2 uppercase">
                      {typeof offer.trajanje === 'object' ? (offer.trajanje[currentLang] || offer.trajanje.bs) : (offer.trajanje || offer.trajanjePaketa)}
                   </span>
                )}
                {offer.opis && (
                  <p className="text-gray-600 mt-4 text-sm leading-relaxed font-medium italic border-l-4 border-gray-900 pl-4">
                    {typeof offer.opis === 'object' ? (offer.opis[currentLang] || offer.opis.bs) : offer.opis}
                  </p>
                )}
              </div>

              <div className="md:text-right flex flex-col justify-start md:items-end">
                <h3 className="text-[12px] tracking-[0.4em] font-black text-gray-900 mb-2 uppercase">{t('clientOffer.investment')}</h3>
                <div className="text-5xl font-black font-serif text-gray-900 leading-none">
                  {offer.cijena} <span className="text-lg font-sans font-black text-gray-400">{offer.valuta}</span>
                </div>
                {offer.popust && offer.popust.trim() && (
                  <div className="mt-3 text-[11px] font-black text-emerald-700 tracking-[0.2em] bg-emerald-50 border-2 border-emerald-100 px-4 py-2 rounded-full inline-block shadow-sm">
                    {offer.popust}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 space-y-6 pt-10 border-t-2 border-gray-900">
                <h3 className="text-[10px] tracking-[0.3em] font-black text-gray-400 uppercase">{t('clientOffer.includes')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-12">
                  {offer.usluge && offer.usluge.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 group">
                      <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[15px] text-gray-900 font-bold group-hover:text-black transition-colors leading-snug">
                        {typeof item === 'object' ? (item[currentLang] || item.bs) : item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {offer.extraOffers && offer.extraOffers.length > 0 && (
                <div className="md:col-span-2 pt-12">
                    <h3 className="text-[10px] tracking-[0.3em] font-black text-gray-400 mb-6 uppercase">{t('clientOffer.addons')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {offer.extraOffers.map((extra, idx) => {
                            const isSelected = selectedExtraOffers.some(e => e.firestoreId === extra.firestoreId);
                            return (
                              <div 
                                key={idx} 
                                onClick={() => toggleExtraOffer(extra)}
                                className={`p-5 rounded-3xl flex justify-between items-center group transition-all cursor-pointer border-2 ${
                                  isSelected 
                                    ? 'bg-gray-900 border-gray-900 text-white shadow-xl scale-[1.02]' 
                                    : 'bg-white border-gray-200 hover:border-gray-900 text-gray-900'
                                }`}
                              >
                                <div className="flex items-center justify-between pointer-events-none">
                                  <div 
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                      isSelected ? 'bg-white border-white text-gray-900' : 'bg-white border-gray-300'
                                    }`}
                                  >
                                        {isSelected && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>}
                                    </div>
                                    <div className="ml-4">
                                        <p className={`font-black text-[15px] mb-0.5 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                          {typeof extra.naziv === 'object' ? (extra.naziv[currentLang] || extra.naziv.bs) : extra.naziv}
                                        </p>
                                        <p className={`text-[10px] tracking-widest font-black uppercase font-sans ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                                          {t('clientOffer.option')}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                   <span className={`text-[16px] font-black ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                     +{extra.cijena} <small className="text-[10px] opacity-70 uppercase font-bold">{extra.valuta}</small>
                                   </span>
                                </div>
                              </div>
                            );
                        })}
                    </div>
                </div>
              )}

              {offer.dodatneInformacije && (
                <div className="md:col-span-2 pt-10 border-t-2 border-gray-900">
                    <h3 className="text-[10px] tracking-[0.3em] font-black text-gray-400 mb-4 uppercase">{t('clientOffer.notes')}</h3>
                    <p className="text-sm text-gray-900 leading-relaxed font-bold bg-gray-100 p-6 rounded-2xl border border-gray-200">
                      {offer.dodatneInformacije}
                    </p>
                </div>
              )}

              <div className="md:col-span-2 pt-16 border-t-2 border-gray-900 mt-10">
                <div className="">
                    <h3 className="text-2xl font-black font-serif text-gray-900 mb-8 text-center leading-tight uppercase tracking-tight">{t('clientOffer.confirmation')}</h3>
                    
                    {offer.privacyOption?.enabled && (
                      <div className={`mb-10 p-6 rounded-3xl border-2 transition-all cursor-pointer ${
                        isPrivacyEnabled 
                          ? 'bg-gray-900 border-gray-900 text-white shadow-xl scale-[1.01]' 
                          : 'bg-white border-gray-200 hover:border-gray-900 text-gray-900'
                      }`}
                           onClick={() => setIsPrivacyEnabled(!isPrivacyEnabled)}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex gap-5 items-center">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl transition-all ${
                              isPrivacyEnabled ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-400'
                            }`}>
                              {isPrivacyEnabled ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
                              ) : '🔒'}
                            </div>
                            <div>
                               <h4 className="font-black mb-0.5 uppercase tracking-tight">{t('clientOffer.privacyTitle')}</h4>
                               <p className={`text-[13px] font-bold leading-relaxed ${isPrivacyEnabled ? 'text-white/80' : 'text-gray-500'}`}>
                                 {t('clientOffer.privacyDesc')}
                               </p>
                            </div>
                          </div>
                          <div className={`text-right sm:border-l pl-6 shrink-0 ${isPrivacyEnabled ? 'border-white/20' : 'border-gray-100'}`}>
                             <div className={`text-[10px] font-black tracking-widest mb-1 uppercase ${isPrivacyEnabled ? 'text-white/60' : 'text-gray-400'}`}>
                               {t('clientOffer.additionalFee')}
                             </div>
                             <div className={`text-2xl font-black ${isPrivacyEnabled ? 'text-white' : 'text-gray-900'}`}>
                               +{offer.privacyOption.price} <span className="text-xs uppercase opacity-70">{offer.privacyOption.valuta}</span>
                             </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col items-center mb-10 pb-10 border-b-4 border-gray-900">
                        <span className="text-[12px] font-black text-gray-900 tracking-[0.5em] mb-4 uppercase">{t('clientOffer.totalInvestment')}</span>
                        <div className="text-8xl md:text-9xl font-black font-serif text-gray-900 leading-none tracking-tighter shadow-text">
                            {calculateTotal()} <span className="text-2xl font-sans font-black text-gray-400 align-top uppercase">{offer.valuta}</span>
                        </div>
                    </div>

                    {!rejectMode ? (
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-12 mt-12 w-full">
                          <button
                            onClick={() => setRejectMode(true)}
                            className="text-gray-400 font-black tracking-[0.3em] text-[11px] hover:text-red-600 transition-all font-sans uppercase border-b-2 border-transparent hover:border-red-600 pb-1 whitespace-nowrap"
                          >
                            {t('clientOffer.reject')}
                          </button>
                          <button
                            onClick={() => handleDecision('prihvaćeno')}
                            disabled={submitting}
                            className="bg-gray-900 text-white px-12 py-6 rounded-full font-black tracking-[0.4em] text-[12px] hover:bg-gray-800 transition-all disabled:opacity-30 font-sans uppercase shadow-2xl hover:scale-105 active:scale-95"
                          >
                            {submitting ? t('clientOffer.submitting') : t('clientOffer.approve')}
                          </button>
                        </div>
                    ) : (
                      <div className="mt-8 animate-fade-in font-sans w-full max-w-xl mx-auto">
                        <h4 className="text-[11px] font-black text-gray-900 tracking-[0.3em] uppercase mb-10 text-center border-b pb-4">{t('clientOffer.reasonTitle')}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                          {rejectReasons.map((reason, idx) => (
                            <button
                              key={idx}
                              onClick={() => setRejectReason(reason)}
                              className={`p-5 text-[11px] font-black tracking-widest transition-all border-2 rounded-xl h-full flex items-center justify-center text-center ${rejectReason === reason ? 'bg-gray-900 text-white border-gray-900 shadow-xl' : 'bg-white border-gray-100 text-gray-500 hover:border-gray-900 hover:text-gray-900'}`}
                            >
                              {reason}
                            </button>
                          ))}
                        </div>
                        
                        {rejectReason === 'Drugo' && (
                          <textarea
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                            placeholder={t('clientOffer.otherPlaceholder')}
                            className="w-full p-6 border-2 border-gray-100 focus:border-gray-900 focus:ring-0 transition-all text-sm mb-10 bg-gray-50 resize-none font-bold rounded-2xl"
                            rows="4"
                          ></textarea>
                        )}

                        <div className="flex items-center justify-center gap-10">
                           <button 
                             onClick={() => setRejectMode(false)} 
                             className="text-[11px] font-black tracking-[0.3em] text-gray-400 uppercase hover:text-gray-900 transition-colors border-b-2 border-transparent hover:border-gray-900 pb-1"
                           >
                             {t('clientOffer.cancel')}
                           </button>
                           <button 
                             onClick={() => handleDecision('odbijeno')}
                             disabled={submitting || !rejectReason || (rejectReason === 'Drugo' && !otherReason)}
                             className="bg-red-600 text-white px-10 py-5 rounded-full font-black tracking-[0.3em] text-[11px] hover:bg-red-700 transition-all disabled:opacity-30 uppercase shadow-xl"
                           >
                             {submitting ? t('clientOffer.submitting') : t('clientOffer.confirmReject')}
                           </button>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-16 text-center space-y-2 opacity-50">
          </div>
        </div>
      </div>
    </section>
  );
}

export default ClientOffer;

